import { apiFetch } from "./client";
import { getAuthCredentials } from "@/store/useAuthStore";
import { viewAsHeaderValue } from "@/lib/viewAs";

/**
 * D-147 W1 S3 — API-модуль «Бази знань» (Knowledge Library, /knowledge).
 *
 * Reader-контракти S2 (TZ §3/§11/P12): counts/allowedActions/видимість =
 * ВИКЛЮЧНО backend (KbAccessResolver, ONE-PREDICATE); reader-payload НЕ
 * містить draft-ключів; denied == missing → єдиний 404 `resource_not_found`.
 * Download/зображення — secured endpoints, blob + header-auth (bare <a>/<img>
 * у цьому деплої = 401, D-131).
 */

const API_BASE = "/api/v1";

export interface KbPrincipal {
  id: string;
  name: string;
}

export interface KbSectionTile {
  id: string;
  name: string;
  description: string;
  capability: string;
  owner: KbPrincipal | null;
  publishedCount: number;
  /** editor+ only — Reader-payload цього ключа не має (AC-KB-05). */
  draftCount?: number;
}

export interface KbRecentItem {
  itemId: string;
  sectionId: string;
  sectionName: string;
  type: "page" | "file";
  title: string;
  lastPublishedAt: string;
  originalFilename?: string;
  fileSize?: number | null;
}

export interface KbHomeResponse {
  sections: KbSectionTile[];
  recent: KbRecentItem[];
  homeActions: { createSection: boolean };
}

export interface KbFolder {
  id: string;
  name: string;
  parentFolderId: string | null;
  sortOrder: number;
}

export interface KbFolderCounts {
  publishedCount?: number;
  draftCount?: number;
}

export interface KbTeamRef {
  id: string;
  name: string;
  /** S5: команда видалена — DENY-семантика збережена, chip «недоступна». */
  missing?: boolean;
}

export interface KbSectionResponse {
  id: string;
  name: string;
  description: string;
  capability: string;
  isArchived: boolean;
  owner: KbPrincipal | null;
  backupOwner: KbPrincipal | null;
  folders: KbFolder[];
  /** folderId ('' = без папки) → counts. */
  folderCounts: Record<string, KbFolderCounts>;
  sectionActions: Record<string, boolean>;
  /** owner-grade only. */
  visibilityMode?: "all" | "teams";
  editors?: KbPrincipal[];
  teams?: KbTeamRef[];
}

export interface KbListItem {
  itemId: string;
  type: "page" | "file";
  title: string;
  folderId: string | null;
  isPinned: boolean;
  pinnedOrder: number | null;
  lastPublishedAt: string;
  originalFilename?: string;
  fileSize?: number | null;
  owner: KbPrincipal | null;
  /** scope=all (editor+) — draft-стан рядка. */
  status?: "draft" | "published" | "archived";
  state?: KbDraftState;
  hasUnpublishedChanges?: boolean;
  draftTitle?: string | null;
  draftFolderId?: string | null;
  draftRevision?: number | null;
  draftModifiedAt?: string;
}

export type KbDraftState = "no_draft" | "draft_exists" | "no_changes";

/** Рядок «Мої чернетки» / «Чернетки розділу» (S2 my-drafts/section-drafts). */
export interface KbDraftRow {
  itemId: string;
  sectionId: string;
  sectionName: string;
  type: "page" | "file";
  status: string;
  draftTitle: string;
  publishedTitle: string;
  draftRevision: number | null;
  draftModifiedAt: string;
  draftModifiedById: string;
  /** S5-дельта P3 (additive). */
  draftModifiedByName?: string;
  ownerId?: string;
  ownerName?: string;
}

export interface KbItemsResponse {
  sectionId: string;
  scope: string;
  items: KbListItem[];
}

/** P2 per-item allowedActions — FE НІЧОГО не виводить сам (нуль інференсів). */
export interface KbItemActions {
  edit: boolean;
  createDraft: boolean;
  publish: { allowed: boolean; reasonCode: string | null; reasonText: string | null };
  archive: boolean;
  restore: boolean;
  pin: boolean;
  unpin: boolean;
}

export interface KbItemResponse {
  itemId: string;
  sectionId: string;
  sectionName: string;
  type: "page" | "file";
  status: string;
  isPinned: boolean;
  owner: KbPrincipal | null;
  actions: KbItemActions;
  published?: {
    title: string;
    folderId: string | null;
    lastPublishedAt: string;
    publishedBy: KbPrincipal | null;
    contentHtml?: string;
  };
  file?: {
    originalFilename: string;
    size: number | null;
    mime: string | null;
  };
  /** editor+ only (AC-KB-01/05: reader-payload цих ключів не має). */
  state?: KbDraftState;
  hasUnpublishedChanges?: boolean;
  draft?: {
    revision: number;
    title: string;
    folderId: string | null;
    modifiedAt: string;
    modifiedBy: KbPrincipal | null;
    contentJson?: unknown;
    contentHtml?: string;
  };
}

export interface KbSearchResult {
  itemId: string;
  sectionId: string;
  sectionName: string;
  type: "page" | "file";
  title: string;
  scope: "published" | "draft";
  snippet: string;
  modifiedAt: string;
}

export interface KbSearchResponse {
  query: string;
  scope: string;
  results: KbSearchResult[];
}

export function fetchKbHome(signal?: AbortSignal): Promise<KbHomeResponse> {
  return apiFetch<KbHomeResponse>("/Custom/knowledge/home", { signal });
}

export function searchKb(
  q: string,
  scope: "published" | "drafts" = "published",
  signal?: AbortSignal,
): Promise<KbSearchResponse> {
  return apiFetch<KbSearchResponse>("/Custom/knowledge/search", {
    query: scope === "published" ? { q } : { q, scope },
    signal,
  });
}

export function fetchKbMyDrafts(signal?: AbortSignal): Promise<{ items: KbDraftRow[] }> {
  return apiFetch<{ items: KbDraftRow[] }>("/Custom/knowledge/my-drafts", { signal });
}

export function fetchKbSectionDrafts(
  sectionId: string,
  signal?: AbortSignal,
): Promise<{ items: KbDraftRow[] }> {
  return apiFetch<{ items: KbDraftRow[] }>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/drafts`,
    { signal },
  );
}

/** scope=all — editor+ (published + drafts + draft-стани; server-side reject для reader). */
export function fetchKbSectionItemsAll(
  sectionId: string,
  signal?: AbortSignal,
): Promise<KbItemsResponse> {
  return apiFetch<KbItemsResponse>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/items`,
    { query: { scope: "all" }, signal },
  );
}

export function fetchKbSection(sectionId: string, signal?: AbortSignal): Promise<KbSectionResponse> {
  return apiFetch<KbSectionResponse>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}`,
    { signal },
  );
}

export function fetchKbSectionItems(sectionId: string, signal?: AbortSignal): Promise<KbItemsResponse> {
  return apiFetch<KbItemsResponse>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/items`,
    { signal },
  );
}

export function fetchKbItem(itemId: string, signal?: AbortSignal): Promise<KbItemResponse> {
  return apiFetch<KbItemResponse>(
    `/Custom/knowledge/items/${encodeURIComponent(itemId)}`,
    { signal },
  );
}

/** Secured streaming-URL основного файлу (blob + header-auth). */
export function kbFileUrl(itemId: string): string {
  return `${API_BASE}/Custom/knowledge/items/${encodeURIComponent(itemId)}/file`;
}

/** «опубліковано 18.07.2026» — dd.MM.yyyy. */
export function formatKbDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** «PDF» з originalFilename (для «файл PDF · 0,4 МБ»). */
export function kbFileExt(originalFilename?: string): string {
  const ext = (originalFilename ?? "").split(".").pop() ?? "";
  return ext.length >= 2 && ext.length <= 5 ? ext.toUpperCase() : "";
}

// ── S4: мутації (write-шляхи S2; idempotency requestId [A-Za-z0-9_-]{8,64}) ──

export function newKbRequestId(): string {
  const rnd = crypto.getRandomValues(new Uint8Array(12));
  return "kbfe_" + Array.from(rnd, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** 409-body дублікатів: {code, existing:{itemId,title}} (S2-контракт). */
export interface KbDuplicateExisting {
  itemId: string;
  title: string;
}

export interface KbCreateResult {
  itemId: string;
  draftRevision: number;
  idempotentReplay?: boolean;
}

export function createKbPage(
  sectionId: string,
  data: { title: string; folderId?: string | null; requestId: string; confirmDuplicateTitle?: boolean },
): Promise<KbCreateResult> {
  const body: Record<string, unknown> = { title: data.title, requestId: data.requestId };
  if (data.folderId) body.folderId = data.folderId;
  if (data.confirmDuplicateTitle) body.confirmDuplicateTitle = true;
  return apiFetch<KbCreateResult>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/pages`,
    { method: "POST", body },
  );
}

export function createKbDraft(itemId: string): Promise<{ draftRevision: number }> {
  return apiFetch<{ draftRevision: number }>(
    `/Custom/knowledge/items/${encodeURIComponent(itemId)}/create-draft`,
    { method: "POST", body: {} },
  );
}

export function saveKbDraft(
  itemId: string,
  data: {
    expectedDraftRevision: number;
    draftTitle?: string;
    draftFolderId?: string | null;
    contentJson?: unknown;
  },
): Promise<{ draftRevision: number; hasUnpublishedChanges: boolean }> {
  return apiFetch(`/Custom/knowledge/items/${encodeURIComponent(itemId)}/draft`, {
    method: "POST",
    body: data,
  });
}

export function publishKbItem(
  itemId: string,
  data: { expectedDraftRevision: number; requestId: string; confirmDuplicateTitle?: boolean },
): Promise<{ itemId: string; status: string; publishedTitle: string; idempotentReplay?: boolean }> {
  const body: Record<string, unknown> = {
    expectedDraftRevision: data.expectedDraftRevision,
    requestId: data.requestId,
  };
  if (data.confirmDuplicateTitle) body.confirmDuplicateTitle = true;
  return apiFetch(`/Custom/knowledge/items/${encodeURIComponent(itemId)}/publish`, {
    method: "POST",
    body,
  });
}

// ── multipart-upload (XHR: header-auth + X-XSRF-Token, D-146 W2 дзеркало) ────

export class KbUploadError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly existing?: KbDuplicateExisting,
  ) {
    super(message);
    this.name = "KbUploadError";
  }
}

function kbMultipartPost<T>(
  path: string,
  form: FormData,
  onProgress?: (loaded: number, total: number) => void,
): { promise: Promise<T>; abort: () => void } {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    xhr.open("POST", new URL(`${API_BASE}${path}`, window.location.origin).toString());
    xhr.responseType = "text";
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    const creds = getAuthCredentials();
    if (creds) {
      const enc = btoa(unescape(encodeURIComponent(`${creds.userName}:${creds.token}`)));
      xhr.setRequestHeader("Espo-Authorization", enc);
      xhr.setRequestHeader("Authorization", `Basic ${enc}`);
      xhr.setRequestHeader("Espo-Authorization-By-Token", "true");
    }
    const viewAs = viewAsHeaderValue();
    if (viewAs) xhr.setRequestHeader("X-Kormax-View-As", viewAs);
    // F0A CSRF guard: мутації несуть X-XSRF-Token (дзеркало apiFetch)
    const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
    if (xsrf) xhr.setRequestHeader("X-XSRF-Token", decodeURIComponent(xsrf[1]));

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300 && body) {
        resolve(body as T);
        return;
      }
      const flat = (body ?? {}) as { code?: string; message?: string; existing?: KbDuplicateExisting };
      reject(
        new KbUploadError(
          xhr.status,
          flat.code ?? `http_${xhr.status}`,
          flat.message || xhr.getResponseHeader("X-Status-Reason") || "Не вдалося завантажити файл.",
          flat.existing,
        ),
      );
    };
    xhr.onerror = () => reject(new KbUploadError(0, "network", "Мережевий збій. Повторіть."));
    xhr.onabort = () => reject(new KbUploadError(0, "aborted", "Скасовано."));
    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}

/** §6 file-create: результат = ЗАВЖДИ чернетка (публікація — з картки). */
export function uploadKbFile(
  sectionId: string,
  file: File,
  fields: {
    title: string;
    folderId?: string | null;
    requestId: string;
    confirmDuplicateFile?: boolean;
    confirmDuplicateTitle?: boolean;
  },
  onProgress?: (loaded: number, total: number) => void,
): { promise: Promise<KbCreateResult>; abort: () => void } {
  const form = new FormData();
  form.append("title", fields.title);
  if (fields.folderId) form.append("folderId", fields.folderId);
  form.append("requestId", fields.requestId);
  if (fields.confirmDuplicateFile) form.append("confirmDuplicateFile", "true");
  if (fields.confirmDuplicateTitle) form.append("confirmDuplicateTitle", "true");
  form.append("file", file);
  return kbMultipartPost<KbCreateResult>(
    `/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/files`,
    form,
    onProgress,
  );
}

/** §6/P8 content_image: привʼязка до існуючого draft-item (лише jpg/png). */
export function uploadKbContentImage(
  itemId: string,
  file: File,
): Promise<{ attachmentId: string }> {
  const form = new FormData();
  form.append("file", file);
  return kbMultipartPost<{ attachmentId: string }>(
    `/Custom/knowledge/items/${encodeURIComponent(itemId)}/images`,
    form,
  ).promise;
}

/** URL content_image (той самий формат, що рендерер §7). */
export function kbImageUrl(itemId: string, attachmentId: string): string {
  return `${API_BASE}/Custom/knowledge/items/${encodeURIComponent(itemId)}/images/${encodeURIComponent(attachmentId)}`;
}

// ── S5: owner-поверхні / admin / move ────────────────────────────────────────

const itemPost = <T,>(itemId: string, op: string, body: Record<string, unknown> = {}) =>
  apiFetch<T>(`/Custom/knowledge/items/${encodeURIComponent(itemId)}/${op}`, {
    method: "POST",
    body,
  });

/** S5-дельта P2: зміна «Відповідального» (immediate + audit). */
export function changeKbItemOwner(itemId: string, ownerId: string) {
  return itemPost<{ itemId: string; ownerId: string }>(itemId, "owner", { ownerId });
}

export const archiveKbItem = (id: string) => itemPost<{ status: string }>(id, "archive");
export const restoreKbItem = (id: string) => itemPost<{ status: string }>(id, "restore");
export const pinKbItem = (id: string) => itemPost<{ isPinned: boolean }>(id, "pin");
export const unpinKbItem = (id: string) => itemPost<{ isPinned: boolean }>(id, "unpin");

export interface KbMovePreview {
  itemId: string;
  from: { sectionId: string; name: string; visibilityMode: string; teams: string[] };
  to: { sectionId: string; name: string; visibilityMode: string; teams: string[] };
  pinCleared: boolean;
  titleDuplicate: KbDuplicateExisting | null;
}

export function fetchKbMovePreview(itemId: string, targetSectionId: string) {
  return apiFetch<KbMovePreview>(
    `/Custom/knowledge/items/${encodeURIComponent(itemId)}/move-preview`,
    { query: { targetSectionId } },
  );
}

export function moveKbItem(
  itemId: string,
  data: { targetSectionId: string; targetFolderId?: string | null; requestId: string; confirmDuplicateTitle?: boolean },
) {
  const body: Record<string, unknown> = {
    targetSectionId: data.targetSectionId,
    requestId: data.requestId,
  };
  if (data.targetFolderId) body.targetFolderId = data.targetFolderId;
  if (data.confirmDuplicateTitle) body.confirmDuplicateTitle = true;
  return itemPost<{ itemId: string; sectionId: string }>(itemId, "move", body);
}

// folders (owner-grade у сервісі)
export const createKbFolder = (sectionId: string, name: string, parentFolderId?: string | null) =>
  apiFetch<{ id: string }>(`/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/folders`, {
    method: "POST",
    body: parentFolderId ? { name, parentFolderId } : { name },
  });
export const renameKbFolder = (folderId: string, name: string) =>
  apiFetch(`/Custom/knowledge/folders/${encodeURIComponent(folderId)}/rename`, {
    method: "POST",
    body: { name },
  });
export const moveKbFolder = (folderId: string, parentFolderId: string | null) =>
  apiFetch(`/Custom/knowledge/folders/${encodeURIComponent(folderId)}/move`, {
    method: "POST",
    body: parentFolderId ? { parentFolderId } : {},
  });
export const deleteKbFolder = (folderId: string) =>
  apiFetch(`/Custom/knowledge/folders/${encodeURIComponent(folderId)}`, { method: "DELETE" });

// admin-поверхня
export interface KbAdminSection {
  id: string;
  name: string;
  description: string;
  owner: KbPrincipal | null;
  backupOwner: KbPrincipal | null;
  visibilityMode: "all" | "teams";
  isArchived: boolean;
  sortOrder: number;
  editors: KbPrincipal[];
  teams: KbTeamRef[];
  publishedCount: number;
  draftCount: number;
}

export function fetchKbAdminSections() {
  return apiFetch<{ sections: KbAdminSection[]; isKbAdmin: boolean }>(
    "/Custom/knowledge/admin/sections",
  );
}

export function createKbSection(data: {
  name: string;
  description?: string;
  ownerId: string;
  backupOwnerId?: string;
  visibilityMode: "all" | "teams";
  teamIds?: string[];
  editorIds?: string[];
}) {
  return apiFetch<{ id: string }>("/Custom/knowledge/admin/sections", { method: "POST", body: data });
}

const sectionPost = (sectionId: string, op: string, body: Record<string, unknown> = {}) =>
  apiFetch(`/Custom/knowledge/sections/${encodeURIComponent(sectionId)}/${op}`, {
    method: "POST",
    body,
  });

export const updateKbSectionSettings = (id: string, data: { name?: string; description?: string }) =>
  sectionPost(id, "settings", data);
export const changeKbSectionOwner = (id: string, ownerId: string, backupOwnerId: string | null) =>
  sectionPost(id, "owner", backupOwnerId ? { ownerId, backupOwnerId } : { ownerId });
export const setKbSectionEditors = (id: string, editorIds: string[]) =>
  sectionPost(id, "editors", { editorIds });
export const setKbSectionVisibility = (id: string, visibilityMode: "all" | "teams", teamIds: string[]) =>
  sectionPost(id, "visibility", { visibilityMode, teamIds });
export const archiveKbSection = (id: string) => sectionPost(id, "archive");
export const restoreKbSection = (id: string) => sectionPost(id, "restore");
export const orderKbSections = (orderedIds: string[]) =>
  apiFetch("/Custom/knowledge/admin/sections/order", { method: "POST", body: { orderedIds } });

export function fetchKbTeams() {
  return apiFetch<{ teams: { id: string; name: string; memberCount: number }[] }>(
    "/Custom/knowledge/admin/teams",
  );
}
export function searchKbEligibleUsers(q: string) {
  return apiFetch<{ users: KbPrincipal[] }>("/Custom/knowledge/admin/eligible-users", {
    query: { q },
  });
}
export function fetchKbAudience(teamIds: string[]) {
  return apiFetch<{ count: number }>("/Custom/knowledge/admin/audience", {
    method: "POST",
    body: { teamIds },
  });
}
